import { Tag, Callout } from '@dev-spendesk/grapes';
import { isNotNullable } from '@spendesk/general-type-helpers/misc';
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
} from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { CurrentMapping } from 'src/core/modules/accounting-integration/components/CurrentMapping';

import {
  type InternalValue,
  type AnalyticalFieldMappingV2,
  type AnalyticalFieldValueMappingV2,
  type AnalyticalFieldMappingFieldType,
} from '../../../../bookkeep/settings/accounting';
import { useAccountingSoftwareName } from '../../../apis';
import { type SyncMode } from '../../../apis/useSetSyncSettings';
import { AnalyticalFieldSelector } from '../AnalyticalFieldSelector';
import { AnalyticalFieldValueOption } from '../AnalyticalFieldValueOption';

export type Props<
  TField extends InternalValue,
  TValue extends InternalValue,
> = {
  internalField: TField;
  internalValues: TValue[];
  fieldType: AnalyticalFieldMappingFieldType;
  syncMode?: SyncMode;
  analyticalFieldMappings: AnalyticalFieldMappingV2[];
  isIntegrationNameLoading: boolean;
  onDelete?: (valueToDelete: TValue) => void;
  fieldMapping: AnalyticalFieldMappingV2 | undefined;
  setFieldMapping: Dispatch<
    SetStateAction<AnalyticalFieldMappingV2 | undefined>
  >;
  fieldMappingValues: AnalyticalFieldValueMappingV2[];
  setFieldMappingValues: Dispatch<
    SetStateAction<AnalyticalFieldValueMappingV2[]>
  >;
  validateValues?: () => void;
  hideFieldSelector?: boolean;
  hideBorder?: boolean;
  showTitle?: boolean;
  hasError?: boolean;
};

export const FieldValuesMapping = <
  TField extends InternalValue,
  TValue extends InternalValue,
>({
  internalField,
  syncMode,
  fieldType,
  analyticalFieldMappings,
  internalValues,
  isIntegrationNameLoading,
  fieldMapping,
  setFieldMapping,
  fieldMappingValues,
  setFieldMappingValues,
  validateValues,
  onDelete,
  hideFieldSelector = false,
  hideBorder = false,
  showTitle = false,
  hasError = false,
}: Props<TField, TValue>) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();

  const mappedValues = useMemo(
    () =>
      new Set(
        (fieldMapping?.values ?? [])
          .map(({ internalId }) => internalId)
          .filter(isNotNullable),
      ),
    [fieldMapping],
  );

  const isDeletable = !!onDelete;

  const unMappedValues = useMemo(
    () => internalValues.filter(({ id }) => !mappedValues.has(id)),
    [mappedValues, internalValues],
  );

  const handleFieldMappingSelected = useCallback(
    (mapping: AnalyticalFieldMappingV2 | undefined) => {
      if (mapping) {
        setFieldMapping({
          ...mapping,
          internalId: internalField.id,
          fieldType,
        });
        setFieldMappingValues(mapping.values);
      } else {
        setFieldMapping(undefined);
        setFieldMappingValues([]);
      }
    },
    [internalField, fieldType],
  );

  const handleSelectedValue = (
    value: AnalyticalFieldValueMappingV2 | undefined,
    internalId: string,
  ) => {
    setFieldMappingValues((previous) => {
      const copy = [...previous];

      const previouslyMappedValueIndex = previous.findIndex(
        (item) => item.internalId === internalId,
      );

      if (previouslyMappedValueIndex > -1) {
        copy[previouslyMappedValueIndex] = {
          ...previous[previouslyMappedValueIndex],
          internalId: null,
        };
      }

      if (!value) {
        return copy;
      }

      const selectedIndex = previous.findIndex(({ id }) => id === value.id);
      if (selectedIndex > -1) {
        copy[selectedIndex] = {
          ...value,
          internalId,
        };
      }

      return copy;
    });

    if (validateValues) {
      validateValues();
    }
  };

  return (
    <>
      <div className="AnalyticalFieldMappingConfiguration h-full text-left">
        {showTitle && (
          <p className="text-content-primary heading-l mb-32 text-center">
            {t(
              'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.mappingTitle',
            )}
          </p>
        )}

        <Tag variant="purple">{internalField.name}</Tag>
        <div className="mt-8 mb-12 ml-16">
          <CurrentMapping
            isLoading={isIntegrationNameLoading}
            fieldName={fieldMapping?.externalName}
            syncMode={syncMode}
          />
        </div>

        {syncMode !== 'none' && !hideFieldSelector && (
          <div className="my-24">
            <AnalyticalFieldSelector
              fieldName={internalField.name}
              fields={analyticalFieldMappings}
              onValueSelected={handleFieldMappingSelected}
              fieldMapping={fieldMapping}
            />
          </div>
        )}

        {syncMode === 'none' && (
          <div className="mt-24">
            <Callout
              variant="warning"
              title={t(
                'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.noSyncValues',
                { integration: accountingSoftwareName },
              )}
            />
          </div>
        )}

        {syncMode === 'pull' && fieldMappingValues.length > 1 && (
          <div className="my-24">
            <Callout
              title={t(
                'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.autoMapCallout',
                { integration: accountingSoftwareName },
              )}
            />
          </div>
        )}

        {hasError && (
          <div className="my-24">
            <Callout
              variant="alert"
              title={t(
                'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.errorAllFieldsRequired',
              )}
            />
          </div>
        )}

        {fieldMapping && syncMode !== 'none' && (
          <div className="h-full max-h-[600px]">
            <div
              className={`grid ${!hideBorder && 'border-b'} ${isDeletable ? 'grid-cols-[1fr_16px_1fr_40px]' : 'grid-cols-[1fr_16px_1fr]'} gap-8 border-gray-200 pb-8`}
            >
              <div className="font-medium">
                {t(
                  'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.spendeskValue',
                )}
              </div>
              <div />
              <div className="font-medium">
                {t(
                  'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.integrationValue',
                  { integration: accountingSoftwareName },
                )}
              </div>
              {isDeletable && (
                <div className="flex items-center justify-center font-medium" />
              )}
            </div>

            <div className="h-full flex-1 overflow-y-auto">
              {unMappedValues.map((entity) => {
                const selectedValue = fieldMappingValues.find(
                  (value) => value.internalId === entity.id,
                );
                return (
                  <div
                    key={`AnalyticalFieldValueOption_${entity.id}`}
                    data-testid={entity.id}
                  >
                    <AnalyticalFieldValueOption
                      mappingValues={fieldMappingValues}
                      entity={entity}
                      onValueSelected={(value) =>
                        handleSelectedValue(value, entity.id)
                      }
                      onDelete={() => onDelete?.(entity)}
                      selectedValue={
                        selectedValue
                          ? {
                              ...selectedValue,
                              key: selectedValue.id,
                              label: selectedValue.externalName,
                            }
                          : undefined
                      }
                      hasError={hasError && !selectedValue}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
